import { arktypeResolver } from "@hookform/resolvers/arktype";
import { ORPCError } from "@orpc/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type } from "arktype";
import { Loader2 } from "lucide-react";
import { Controller, useForm } from "react-hook-form";

import { orpc } from "@/client";
import { PersonSearch, PersonSearchSchemas } from "@/components/medico/person-search";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OverlayShell } from "@/components/ui/overlay";
import { format } from "@/lib/format";
import { AgendaLabels } from "@/lib/labels/agenda";

const appointmentType = type.enumerated(
	"Consulta",
	"Retorno",
	"Cirurgia",
	"Procedimento",
	"Bloqueio",
);
const appointmentForm = type({
	tipo: appointmentType,
	person: PersonSearchSchemas.chosen.or("null"),
	quick_name: "string",
	quick_phone: "string",
	date: "string",
	start_time: "string",
	duration_minutes: "number",
	notes: "string",
}).narrow((value, ctx) => {
	if (value.tipo === "Bloqueio" || value.person !== null) {
		return true;
	}

	if (value.quick_name.trim() && value.quick_phone.trim()) {
		return true;
	}

	return ctx.mustBe("a person or quick patient");
});

export function AgendaCreateModal({ onClose }: { onClose: () => void }) {
	const queryClient = useQueryClient();
	const { data: availability } = useQuery(orpc.agenda.availability.queryOptions());
	const createPerson = useMutation(orpc.people.save.mutationOptions());
	const createAppointment = useMutation(
		orpc.agenda.create.mutationOptions({
			onSuccess: async () => {
				await queryClient.invalidateQueries({ queryKey: orpc.agenda.key() });
				onClose();
			},
		}),
	);
	const {
		control,
		register,
		handleSubmit,
		setValue,
		watch,
		formState: { isValid },
	} = useForm<typeof appointmentForm.infer>({
		resolver: arktypeResolver(appointmentForm),
		mode: "onChange",
		defaultValues: {
			tipo: "Consulta",
			person: null,
			quick_name: "",
			quick_phone: "",
			date: format.today(),
			start_time: "09:00",
			duration_minutes: 60,
			notes: "",
		},
	});
	const tipo = watch("tipo");
	const person = watch("person");
	const pending = createPerson.isPending || createAppointment.isPending;
	const tipoField = register("tipo");
	const appointmentErrorCode =
		createAppointment.error instanceof ORPCError ? createAppointment.error.code : undefined;

	const submit = handleSubmit(async (value) => {
		let personId = value.person?.kind === "patient" ? value.person.patientId : value.person?.leadId;

		if (value.tipo !== "Bloqueio" && personId === undefined) {
			const created = await createPerson.mutateAsync({
				name: value.quick_name,
				phones: [value.quick_phone],
			});
			personId = created.person_id;
		}

		await createAppointment.mutateAsync({
			tipo: value.tipo,
			person_id: personId,
			date: value.date,
			start_time: value.start_time,
			duration_minutes: value.duration_minutes,
			notes: value.notes,
		});
	});

	return (
		<OverlayShell
			label="Novo horário"
			onClose={onClose}
			className="items-center justify-center p-4"
		>
			<form onSubmit={submit} className="overlay-panel max-w-lg gap-4 p-5">
				<h2 className="type-section-title text-foreground">Novo horário</h2>

				<label className="flex flex-col gap-1.5 text-sm font-medium">
					Tipo
					<select
						{...tipoField}
						onChange={(event) => {
							tipoField.onChange(event);

							const next = appointmentType.assert(event.target.value);
							setValue("duration_minutes", availability?.durations[next] ?? 60, {
								shouldValidate: true,
							});
						}}
						className="control-field h-11 md:h-10"
					>
						<option>Consulta</option>
						<option>Retorno</option>
						<option>Cirurgia</option>
						<option>Procedimento</option>
						<option>Bloqueio</option>
					</select>
				</label>

				{tipo !== "Bloqueio" && (
					<div className="flex flex-col gap-3 rounded-lg border border-border p-3">
						<Controller
							control={control}
							name="person"
							render={({ field }) => (
								<PersonSearch chosen={field.value} onChoose={field.onChange} disabled={pending} />
							)}
						/>
						{person === null && (
							<div className="grid gap-3 sm:grid-cols-2">
								<label className="flex flex-col gap-1.5 text-xs font-medium">
									<span>Novo paciente</span>
									<Input {...register("quick_name")} placeholder="Nome" />
								</label>
								<label className="flex flex-col gap-1.5 text-xs font-medium">
									<span>Telefone</span>
									<Input {...register("quick_phone")} placeholder="(31) 99999-9999" />
								</label>
							</div>
						)}
					</div>
				)}

				<div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
					<label className="flex flex-col gap-1.5 text-sm font-medium">
						<span>Data</span>
						<Input {...register("date")} type="date" />
					</label>
					<label className="flex flex-col gap-1.5 text-sm font-medium">
						<span>Início</span>
						<Input {...register("start_time")} type="time" />
					</label>
					<label className="flex flex-col gap-1.5 text-sm font-medium">
						<span>Minutos</span>
						<Input
							{...register("duration_minutes", { valueAsNumber: true })}
							type="number"
							min={5}
							step={5}
						/>
					</label>
				</div>

				<label className="flex flex-col gap-1.5 text-sm font-medium">
					<span>Observação</span>
					<Input {...register("notes")} />
				</label>

				{(createPerson.isError || createAppointment.isError) && (
					<p className="text-sm text-destructive">{AgendaLabels.saveError(appointmentErrorCode)}</p>
				)}

				<div className="flex justify-end gap-2">
					<Button type="button" variant="ghost" onClick={onClose}>
						Cancelar
					</Button>
					<Button type="submit" disabled={!isValid || pending}>
						{pending && <Loader2 className="animate-spin" />}Salvar horário
					</Button>
				</div>
			</form>
		</OverlayShell>
	);
}
